import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate, Link, useSearchParams } from 'react-router-dom'; // ✅ useSearchParams 추가
import { useUser } from '../context/UserContext';

// --- 백엔드 API에서 가져올 데이터 ---
const DEFAULT_API_DATA = {
  totalCapacity: 80,
  earlyBirdCap: null, // 백엔드에서 가져올 값 (설정되지 않으면 null)
  currentMembers: 0,
  registrationCloseDate: null, // 백엔드에서 가져올 값
  earlyBirdDeadline: null,
  registrationDeadline: null,
  eventDateTime: null,
};
// --------------------------------------------------------------------

const getMembershipDetails = (branch, currentPrice = 800) => {
  const baseBenefits = {
    NCCU: [
      "3 SLAM MEETs including Drinks 🍻 and Dinner 🍽️ (Value: 1,200 NTD)",
      "Priority for SLAM Outings & Activities: BBQ, Bowling, Running, etc. ✨ (Value: 200 NTD)",
      "Discounts at Partner Restaurants & Bars 🥂 (Value: 200 NTD)"
    ],
    NTU: [
      "3 Exclusive NTU Chapter MEETs",
      "Joint events with other SLAM branches", 
      "Networking opportunities with top students",
      "Access to all SLAM partnership benefits"
    ],
    TAIPEI: [
      "Monthly themed social events for professionals",
      "Connect with local & international talent",
      "Exclusive partnership deals in the city", 
      "A chance to escape your work bubble"
    ]
  };

  const titles = {
    NCCU: "Elevate Your NCCU Experience with SLAM 🌟",
    NTU: "Supercharge Your NTU Life with SLAM 🚀", 
    TAIPEI: "Expand Your Network in Taipei with SLAM 💼"
  };

  return {
    title: titles[branch],
    benefits: baseBenefits[branch]
  };
};

// ✅ 지부별 계좌 정보 (각 지부마다 다른 계좌)
const branchBankInfo = {
  NCCU: {
    bank: "(822) Cathay United Bank",
    account: "123-456-7890",
    accountName: "SLAM NCCU"
  },
  NTU: {
    bank: "(700) China Post",
    account: "098-765-4321", 
    accountName: "SLAM NTU"
  },
  TAIPEI: {
    bank: "(812) Taiwan Cooperative Bank",
    account: "555-123-9876",
    accountName: "SLAM TAIPEI"
  }
};
// ✅ 통합 대분류 - 모든 학교에서 사용 가능한 표준화된 전공 카테고리 (알파벳 순)
const unifiedMajors = [
  "Agriculture & Life Sciences",
  "Architecture & Planning",
  "Business & Management", 
  "Communication & Media Studies",
  "Computer Science & Information Technology",
  "Design & Arts",
  "Economics & Finance",
  "Education & Teaching",
  "Engineering & Technology",
  "Environmental Studies",
  "Languages & Linguistics", 
  "Law & Legal Studies",
  "Liberal Arts & Humanities",
  "Medicine & Health Sciences",
  "Music & Performing Arts",
  "Philosophy & Religious Studies",
  "Political Science & International Relations",
  "Public Health & Social Work",
  "Science & Mathematics",
  "Social Sciences & Psychology",
  "Sports & Physical Education",
  "Others"
];

const taipeiStatuses = ["Student", "Professional", "Business Owner", "Freelancer", "Intern"];

// ✅ 단순화된 산업 카테고리 (모든 전문직 상태에 공통 적용, 알파벳 순)
const industryCategories = [
  "Design & Creative",
  "Education & Research",
  "Finance & Banking", 
  "Food & Beverage",
  "Government & Public Service",
  "Healthcare & Medical",
  "Legal & Consulting",
  "Manufacturing",
  "Marketing & Advertising",
  "Media & Communications",
  "Non-profit & NGO",
  "Real Estate",
  "Retail & E-commerce",
  "Technology & Software",
  "Others"
];

// ISO-like country list (subset for brevity, can be expanded)
const countryOptions = [
  // Americas (Latin America & South America emphasized)
  "Argentina", "Belize", "Bolivia", "Brazil", "Chile", "Colombia", "Costa Rica", "Cuba", "Dominican Republic", "Ecuador", "El Salvador", "Guatemala", "Guyana", "Haiti", "Honduras", "Jamaica", "Mexico", "Nicaragua", "Panama", "Paraguay", "Peru", "Puerto Rico", "Suriname", "Trinidad and Tobago", "Uruguay", "Venezuela",
  // Others (existing)
  "Australia", "Austria", "Belgium", "Canada", "China", "Denmark", "Finland", "France", "Germany", "Hong Kong", "India", "Indonesia", "Ireland", "Italy", "Japan", "Malaysia", "Netherlands", "New Zealand", "Norway", "Philippines", "Poland", "Portugal", "Russia", "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey", "United Arab Emirates", "United Kingdom", "United States", "Vietnam"
].sort().concat("— Others"); // ✅ Others 옵션을 마지막에 추가

// --- 새로운 반응형 SLAM 프로모션 카드 ---
const SlamPromotionCard = ({ data, onRegisterClick, buttonState, membershipStatus }) => {
    const { totalCapacity, earlyBirdCap, currentMembers, registrationCloseDate, selectedBranch, currentPrice } = data;
  
  // 백엔드에서 계산한 값을 우선적으로 사용
  const isEarlyBirdActive = data.isEarlyBirdActive !== undefined ? data.isEarlyBirdActive : false;
  const price = data.currentPrice !== undefined ? data.currentPrice : (isEarlyBirdActive ? (data.earlyBirdPrice || 800) : (data.regularPrice || 900));
  const displayPrice = price === 0 ? 'FREE' : `NT$${price.toLocaleString()}`;
  

  // ✅ Capacity Warning 로직 사용 (백엔드에서 계산된 값 우선 사용)
  // 0은 falsy 값이므로 || 대신 ?? 사용
  const capacityWarningBase = data.capacityWarningBase ?? currentMembers;
  const capacityWarningLimit = data.capacityWarningLimit ?? totalCapacity;
  const spotsLeft = capacityWarningLimit - capacityWarningBase;
  

    // 남은 시간 계산 - 3단계 로직 적용
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      
      // 3단계 로직:
      // 1) Early Bird 진행 중 & Date 설정됨: earlyBirdEndDate 사용
      // 2) Early Bird 진행 중 & Date 설정 안됨: 카운트다운 표시 안함 (capacity만 표시)
      // 3) Early Bird 종료 후: registrationDeadline 사용 (이벤트의 등록 마감일)
      // 4) registrationDeadline이 없으면: eventDateTime 사용 (이벤트 시작 시간)
      let deadline;
      
      // Early Bird가 활성화되었지만 Date가 설정되지 않은 경우 (Capacity만 사용)
      if (isEarlyBirdActive && !data.earlyBirdDeadline) {
        setTimeLeft(""); // 카운트다운 표시 안함
        return;
      }
      
      if (isEarlyBirdActive && data.earlyBirdDeadline) {
        deadline = new Date(data.earlyBirdDeadline);
      } else {
        // Early Bird 종료 후: registrationDeadline 우선, 없으면 eventDateTime
        deadline = new Date(data.registrationDeadline || data.eventDateTime);
      }
      
      const diff = deadline - now;
      if (diff <= 0) {
        if (isEarlyBirdActive) {
          // Early Bird가 끝났을 때는 시간만 보여주고 계속 카운트다운
          setTimeLeft("Early Bird Ended!");
          return;
        } else {
          // Registration 마감 시에는 timeLeft를 빈 문자열로 설정 (별도 메시지 박스에서 처리)
          setTimeLeft("");
          return;
        }
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s left`);
    }, 1000);
    return () => clearInterval(interval);
  }, [data.earlyBirdDeadline, data.registrationDeadline, data.eventDateTime, isEarlyBirdActive]);

  // Registration deadline 확인 (백엔드에서 계산된 값 우선 사용, 없으면 로컬 계산)
  let isRegistrationClosed = data.isRegistrationClosed !== undefined ? data.isRegistrationClosed : false;
  
  // 백엔드 값이 없으면 로컬에서 계산
  if (data.isRegistrationClosed === undefined && data.registrationDeadline) {
    const now = new Date();
    const registrationDeadline = new Date(data.registrationDeadline);
    isRegistrationClosed = now > registrationDeadline;
  }
  
  // Debug code removed
  
  let urgencyMessage;
  // ✅ 멤버십 사용자(승인된 멤버십을 가진 사용자)에게는 경고 메시지 표시하지 않음
  const isMember = membershipStatus && membershipStatus.status === 'APPROVED';
  
  // Registration이 마감되었거나 멤버십 사용자면 Capacity Warning을 보여주지 않음
  if (isRegistrationClosed || isMember) {
    urgencyMessage = null; // 마감 후 또는 멤버십 사용자에게는 메시지 표시 안함
  } else if (spotsLeft <= 0) {
    urgencyMessage = (
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 border-l-4 border-gray-500 text-gray-700 p-4 sm:p-6 rounded-lg animate-pulse">
        <p className="font-bold text-lg">Registration for this semester is now closed.</p>
        <p className="text-sm mt-2">Sign up to get notified for the next season!</p>
      </div>
    );
  } else if (isEarlyBirdActive) {
    const earlyBirdCap = data.earlyBirdCapacity;
    const earlyBirdSpotsLeft = earlyBirdCap ? earlyBirdCap - capacityWarningBase : 0;
    urgencyMessage = (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700 p-4 sm:p-6 rounded-lg">
        {/* Early Bird가 활성화되면 항상 기본 메시지 표시 */}
        <p className="font-bold text-lg">🔥 Early Bird Special is now active!</p>
        {/* Early Bird Capacity가 설정되어 있고 0보다 클 때 추가로 spots left 표시 */}
        {data.earlyBirdUseCapacity && earlyBirdCap && earlyBirdCap > 0 && (
          <p className="font-bold text-lg mt-2">Only {Math.max(0, earlyBirdSpotsLeft)} spots left!</p>
        )}
        <p className="text-sm mt-2">Join now for only {data.earlyBirdPrice === 0 ? 'FREE' : `${data.earlyBirdPrice !== undefined ? data.earlyBirdPrice : 800} NTD`} before the price increases.</p>
      </div>
    );
  } else if (spotsLeft > 10) {
    urgencyMessage = (
      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-500 text-yellow-700 p-4 sm:p-6 rounded-lg">
        <p className="font-bold text-lg">🎉 Early Bird SOLD OUT!</p>
        <div className="mt-3">
          <p className="text-sm">Standard registration is now open.</p>
          <div className="mt-3 bg-yellow-200 border-2 border-yellow-400 rounded-lg p-3 text-center">
            <p className="text-xs mb-1">Form closes in</p>
            <span className="font-bold text-lg sm:text-xl text-yellow-900">{timeLeft}</span>
          </div>
        </div>
      </div>
    );
  } else {
    urgencyMessage = (
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 text-red-700 p-4 sm:p-6 rounded-lg animate-pulse">
        <p className="font-bold text-lg">😱 LAST CHANCE! Only {spotsLeft} spots left!</p>
        <p className="text-sm mt-2">Secure your spot before the semester is fully booked.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl mx-auto mb-8 group">
      {/* 메인 카드 */}
      <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden group-hover:shadow-2xl">
        
        {/* 헤더 섹션 - 파란색 그라데이션 */}
        <div className="relative bg-gradient-to-br from-blue-500 to-blue-700 text-white p-8 sm:p-10 lg:p-12 overflow-hidden">
          {/* 떠다니는 배경 효과 */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white bg-opacity-5 rounded-full animate-bounce"></div>
          
          {/* 아이콘 */}
          <div className="text-center mb-4">
            <div className="text-5xl sm:text-6xl lg:text-7xl mb-2 animate-bounce">🎯</div>
          </div>
          
          {/* 제목 */}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-center mb-2 leading-tight">
            {getMembershipDetails(selectedBranch, price).title}
          </h1>
          
          {/* 부제목 */}
          <p className="text-sm sm:text-base lg:text-lg text-center opacity-90 font-medium">
            Exclusive membership benefits await
          </p>
        </div>

        {/* 가격 섹션 */}
        <div className="p-6 sm:p-8 lg:p-10">
          {(() => {
            const earlyBirdPrice = data.earlyBirdPrice !== undefined ? data.earlyBirdPrice : 800;
            const regularPrice = data.regularPrice !== undefined ? data.regularPrice : 900;
            const earlyBirdCap = data.earlyBirdCap;
            const currentMembers = data.currentMembers || 0;
            const capacityWarningBase = data.capacityWarningBase ?? currentMembers;
            
            // 백엔드에서 계산한 값을 우선적으로 사용
            const isEarlyBirdActive = data.isEarlyBirdActive !== undefined ? data.isEarlyBirdActive : false;
            const price = data.currentPrice !== undefined ? data.currentPrice : (isEarlyBirdActive ? earlyBirdPrice : regularPrice);
            
            return (
              <>
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 sm:p-8 mb-6 text-center border-2 border-red-200 relative">
                  {/* Early Bird 배지 - Early Bird 활성화 시에만 표시 */}
                  {isEarlyBirdActive && (
                    <div className="absolute -top-4 -right-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-bounce z-10">
                      🐦 EARLY BIRD!
                    </div>
                  )}

                  <div className="text-sm sm:text-base text-slate-500 line-through mb-1">
                    Total Value: 1600 NTD
                  </div>
                  <div className="text-base sm:text-lg text-slate-400 line-through mb-2">
                    Regular Price: {regularPrice === 0 ? 'FREE' : `${regularPrice} NTD`}
                  </div>
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-red-600 mb-2 animate-pulse text-center">
                    ⚡ {displayPrice}
                  </div>
                  {isEarlyBirdActive ? (
                    <div className="text-lg sm:text-xl font-bold text-orange-600 mb-3">
                      You Save {1600 - price} NTD ({Math.round(((1600 - price) / 1600) * 100)}% OFF!)
                    </div>
                  ) : (
                    <div className="text-lg sm:text-xl font-bold text-blue-600 mb-3">
                      You Save {1600 - price} NTD ({Math.round(((1600 - price) / 1600) * 100)}% OFF!)
                    </div>
                  )}
                  <div className="inline-block bg-gradient-to-r from-red-100 to-orange-100 text-red-700 px-4 py-2 rounded-full text-sm sm:text-base font-semibold border border-red-300">
                    All Included ✨
                  </div>
                </div>

                                 {/* Early Bird 카운트다운 박스 - Early Bird 활성화 & Registration 열림 시에만 표시 */}
                 {isEarlyBirdActive && !isRegistrationClosed && (
                   <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl p-4 sm:p-6 mb-6 text-center">
                     <div className="flex items-center justify-center space-x-2 mb-2">
                       <span className="text-2xl animate-pulse">⏰</span>
                       <span className="text-sm sm:text-base font-bold text-yellow-800">HURRY UP!</span>
                       <span className="text-2xl animate-pulse">⚡</span>
                     </div>
                     {/* Early Bird 메시지 표시 로직 */}
                     <div className="text-lg sm:text-xl font-bold text-yellow-900 mb-1">
                       Early Bird Special is now active!
                     </div>
                     {/* Early Bird Capacity가 설정되어 있고 0보다 클 때 추가로 spots left 표시 */}
                     {data.earlyBirdUseCapacity && earlyBirdCap && earlyBirdCap > 0 && (
                       <div className="text-lg sm:text-xl font-bold text-yellow-900 mb-1">
                         Only {Math.max(0, earlyBirdCap - capacityWarningBase)} spots left
                       </div>
                     )}
                     <div className="text-base sm:text-lg font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-lg inline-block">
                       {timeLeft}
                     </div>
                   </div>
                 )}

                 {/* Early Bird 종료 메시지 - Early Bird 비활성화 & Registration 아직 열림 & Early Bird가 이전에 활성화되었던 경우 */}
                 {!isEarlyBirdActive && !isRegistrationClosed && data.earlyBirdPrice && (
                   <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 sm:p-6 mb-6 text-center">
                     <div className="flex items-center justify-center space-x-2 mb-2">
                       <span className="text-2xl">🎉</span>
                       <span className="text-sm sm:text-base font-bold text-blue-800">EARLY BIRD ENDED</span>
                       <span className="text-2xl">💳</span>
                     </div>
                     <div className="text-lg sm:text-xl font-bold text-blue-900 mb-1">
                       Regular pricing now applies
                     </div>
                     <div className="text-base sm:text-lg font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-lg inline-block">
                       {timeLeft}
                     </div>
                   </div>
                 )}
                 
                 {/* Registration 마감 메시지 - Registration deadline 지났을 때만 */}
                 {isRegistrationClosed && (
                   <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-4 sm:p-6 mb-6 text-center">
                     <div className="flex items-center justify-center space-x-2 mb-2">
                       <span className="text-2xl">📋</span>
                       <span className="text-sm sm:text-base font-bold text-purple-800">REGISTRATION CLOSED</span>
                       <span className="text-2xl">⏰</span>
                     </div>
                     <div className="text-lg sm:text-xl font-bold text-purple-900 mb-2">
                       Join the waiting list to be notified
                     </div>
                     <div className="text-sm text-purple-700">
                       We'll contact you if spots become available
                     </div>
                   </div>
                 )}

                 {/* Capacity Warning - Early Bird 비활성화 & Registration 아직 열림 시에만 표시 */}
                 {!isEarlyBirdActive && !isRegistrationClosed && data.capacityWarningThreshold && spotsLeft <= data.capacityWarningThreshold && (
                   <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-xl p-4 sm:p-6 mb-6 text-center">
                     <div className="flex items-center justify-center space-x-2 mb-2">
                       <span className="text-2xl animate-pulse">⏰</span>
                       <span className="text-sm sm:text-base font-bold text-red-800">HURRY UP!</span>
                       <span className="text-2xl animate-pulse">⚡</span>
                     </div>
                     <div className="text-lg sm:text-xl font-bold text-red-900 mb-1">
                       Only {spotsLeft} spots left
                     </div>
                     <div className="text-base sm:text-lg font-bold text-red-600 bg-red-100 px-3 py-1 rounded-lg inline-block">
                       {timeLeft}
                     </div>
                   </div>
                 )}


              </>
            );
          })()}

          {/* 혜택 섹션 */}
          <div className="space-y-4 mb-8">
            <h3 className="text-lg sm:text-xl font-bold text-center text-slate-800 mb-4">What's Included:</h3>
            {getMembershipDetails(selectedBranch, price).benefits.map((benefit, i) => {
              // 혜택과 가치를 분리 (더 간단한 로직)
              const hasValue = benefit.includes('(Value:') || benefit.includes('(200NTD)') || benefit.includes('(250NTD)');
              const [mainBenefit, benefitValue] = hasValue ? benefit.split('(') : [benefit, null];
              const value = benefitValue ? `(${benefitValue}` : null;

              return (
                <div
                  key={i}
                  className="flex items-start p-3 sm:p-4 bg-slate-50 rounded-xl transition-all duration-200 hover:translate-x-1 hover:bg-slate-100 border border-slate-100"
                >
                  {/* 체크마크 아이콘 */}
                  <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-3 shadow-sm">
                    <div className="text-white text-xs font-bold">
                      ✓
                    </div>
                  </div>

                  {/* 텍스트 컨텐츠 */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm sm:text-base text-slate-700 font-medium leading-relaxed">
                      {mainBenefit.trim()}
                    </div>
                    {value && (
                      <div className="text-xs text-slate-500 mt-1 font-medium">
                        {value}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Register Now 버튼 */}
          <div className="text-center">
            <button
              onClick={onRegisterClick}
              disabled={buttonState?.disabled}
              className={`text-white font-bold text-lg sm:text-xl py-4 px-8 sm:px-12 rounded-2xl transform transition-all duration-200 shadow-lg hover:shadow-xl ${
                buttonState?.disabled 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : buttonState?.className || 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 hover:scale-105'
              }`}
            >
              <span className="flex items-center justify-center space-x-2">
                <span className="text-2xl">{buttonState?.text === 'Join Waiting List' ? '📋' : '🚀'}</span>
                <span>{buttonState?.text || 'Register Now'}</span>
                <span className="text-2xl">{buttonState?.text === 'Join Waiting List' ? '⏰' : '⚡'}</span>
              </span>
            </button>
            <p className="text-xs text-slate-500 mt-2">
              {buttonState?.text === 'Join Waiting List' 
                ? '📋 Registration is closed. Join the waiting list to be notified of available spots.' 
                : '⚠️ Limited time offer - Early Bird pricing won\'t last!'
              }
            </p>
        </div>
        </div>
      </div>
    </div>
  );
};


export default function MembershipPage() {
  const navigate = useNavigate(); // ✅ 2. navigate 함수를 사용할 수 있도록 설정합니다.
  const { user } = useUser();
  const [searchParams] = useSearchParams(); // ✅ URL 쿼리 파라미터 읽기
  
  // ✅ URL에서 지부 정보가 있으면 미리 선택하고 step 2로 이동, 없으면 에러 표시
  const branchFromUrl = searchParams.get('branch');
  
  const [selectedBranch, setSelectedBranch] = useState(branchFromUrl || '');
  const [step, setStep] = useState(branchFromUrl ? 1 : 0); // 0 = 에러 상태, 1 = 프로모션 카드, 2 = 정보 입력, 3 = 결제
  const [branchCloseDate, setBranchCloseDate] = useState(null);
  const [apiData, setApiData] = useState(DEFAULT_API_DATA);

  // ✅ Staff/admin users should go directly to RSVP (events) instead of membership flow
  useEffect(() => {
    if (user?.isLoggedIn && ['ADMIN', 'STAFF', 'PRESIDENT', 'LEADER'].includes(user.role)) {
      navigate('/events', { replace: true });
    }
  }, [user, navigate]);
  useEffect(() => {
    const fetchCloseDate = async () => {
      if (!selectedBranch) return;
      try {
        const res = await axios.get(`/api/events?branch=${selectedBranch}`);
        const now = new Date();
        const future = (res.data || []).filter(e => new Date(e.eventDateTime) > now);
        if (future.length > 0) {
          const earliest = future.reduce((min, e) => new Date(e.eventDateTime) < new Date(min.eventDateTime) ? e : min, future[0]);
          setBranchCloseDate(new Date(earliest.eventDateTime).toISOString());
        } else {
          setBranchCloseDate(null);
        }
      } catch (e) {
        setBranchCloseDate(null);
      }
    };
    fetchCloseDate();
  }, [selectedBranch]);

  // ✅ 이벤트별 계좌 정보 가져오기 useEffect를 컴포넌트 최상위로 이동
  useEffect(() => {
    // 백엔드에서 받은 은행 정보가 있으면 사용, 없으면 기본 지부 정보 사용
    if (apiData.bankAccount) {
      // 백엔드에서 받은 "Bank - Account - AccountName" 형식을 파싱
      const bankParts = apiData.bankAccount.split(' - ');
      
      if (bankParts.length >= 3) {
        const bankInfo = {
          bank: bankParts[0],
          account: bankParts[1],
          accountName: bankParts[2]
        };
        setEventBankInfo(bankInfo);
      } else {
        // 파싱 실패 시 기본 정보 사용
        setEventBankInfo(branchBankInfo[selectedBranch]);
      }
    } else {
      // 백엔드 정보가 없으면 기본 지부 정보 사용
      setEventBankInfo(branchBankInfo[selectedBranch]);
    }
  }, [selectedBranch, apiData.bankAccount]);

  // ✅ 백엔드에서 멤버십 가격 정보 가져오기
  useEffect(() => {
    const fetchPricingData = async () => {
      if (!selectedBranch) return;
      try {
        // URL에서 eventId 가져오기
        const eventIdFromUrl = searchParams.get('eventId');
        
        let response;
        if (eventIdFromUrl) {
          // 특정 이벤트의 가격 정보 가져오기
          response = await axios.get(`/api/events/${eventIdFromUrl}/membership-pricing?branch=${selectedBranch}`);
        } else {
          // 기존 로직: 가장 최근 이벤트의 가격 정보
          response = await axios.get(`/api/memberships/pricing?branch=${selectedBranch}`);
        }
        
        setApiData({
          ...DEFAULT_API_DATA,
          ...response.data
        });
      } catch (error) {
        // 에러 시 기본 데이터 사용
        setApiData(DEFAULT_API_DATA);
      }
    };
    fetchPricingData();
  }, [selectedBranch, searchParams]);

  // ✅ 사용자의 멤버십 신청 상태 확인
  useEffect(() => {
    const checkMembershipStatus = async () => {
      if (!user?.isLoggedIn || !selectedBranch) return;
      
      try {
        // 사용자의 특정 지부 멤버십 신청 확인
        const response = await axios.get(`/api/memberships/my-application/${selectedBranch}`);
        if (response.data && !response.data.message) {
          setMembershipStatus({
            status: response.data.status,
            applicationId: response.data.id
          });
        } else {
          setMembershipStatus(null);
        }
      } catch (error) {
        setMembershipStatus(null);
      }
    };
    
    checkMembershipStatus();
  }, [user, selectedBranch]);

  // Redirect unauthenticated users to login page
  if (!user?.isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Login required</h1>
          <p className="text-gray-600 mb-6">Please log in to apply for membership.</p>
          <Link 
            to="/login" 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Log in
          </Link>
        </div>
      </div>
    );
  }
  const [paymentMethod, setPaymentMethod] = useState('');
  const [formData, setFormData] = useState({
    userType: '', studentId: '', major: '', otherMajor: '', detailedMajor: '', phone: '', professionalStatus: '',
    industry: '', otherIndustry: '', country: '', otherCountry: '', stayDuration: '', dietaryPreferences: '', dietaryDetails: '', bankLast5: '', 
    howDidYouHear: '', friendReferralName: '', otherSource: '',
    networkingGoal: '', networkingDescription: '', otherNetworkingGoal: ''
  });
  
  // ✅ 이벤트별 계좌 정보 상태를 컴포넌트 최상위로 이동
  const [eventBankInfo, setEventBankInfo] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null); // 사용자의 멤버십 신청 상태
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // LOCAL 선택 시 자동으로 Taiwan 선택
    if (name === 'userType' && value === 'local') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        country: 'Taiwan'  // 자동으로 Taiwan 선택
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ✅ 버튼 상태 및 텍스트 결정 함수
  const getMembershipButtonState = () => {
    // Registration이 마감되었는지 확인 (Early Bird deadline이 아닌 실제 Registration deadline)
    const now = new Date();
    const registrationDeadline = apiData.registrationCloseDate || apiData.registrationDeadline;
    
    // Registration deadline이 설정되어 있고 마감되었을 때만 Waiting List 표시
    if (registrationDeadline) {
      const deadline = new Date(registrationDeadline);
      const isRegistrationClosed = deadline <= now;
      
      if (isRegistrationClosed) {
        return { text: 'Join Waiting List', disabled: false, className: 'bg-purple-600 hover:bg-purple-700' };
      }
    }
    
    if (!membershipStatus) {
      return { text: 'Join Membership', disabled: false, className: 'bg-blue-600 hover:bg-blue-700' };
    }
    
    if (membershipStatus.status === 'payment_pending') {
      return { text: 'Wait for Approval', disabled: false, className: 'bg-yellow-500 hover:bg-yellow-600' };
    }
    
    if (membershipStatus.status === 'APPROVED') {
      return { text: 'View Events & RSVP', disabled: false, className: 'bg-green-500 hover:bg-green-600' };
    }
    
    return { text: 'Join Membership', disabled: false, className: 'bg-blue-600 hover:bg-blue-700' };
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    
    // 기본 필드 검증
    if (selectedBranch === 'TAIPEI') {
      if (!formData.professionalStatus || !formData.phone) {
        alert("Please fill out your Status and Phone Number.");
        return;
      }
      // ✅ 산업 카테고리 검증
      if (formData.professionalStatus !== 'Student' && !formData.industry) {
        alert("Please select your industry.");
        return;
      }
      // ✅ 기타 산업 입력 검증
      if (formData.industry === 'Others' && !formData.otherIndustry) {
        alert("Please specify your industry.");
        return;
      }
      // ✅ Goal 검증
      if (formData.professionalStatus !== 'Student' && !formData.networkingGoal) {
        alert("Please select your main goal.");
        return;
      }
      // ✅ 기타 Goal 입력 검증
      if (formData.networkingGoal === 'Other' && !formData.otherNetworkingGoal) {
        alert("Please specify your goal.");
        return;
      }
      // ✅ 국적 Others 선택 시 상세 입력 검증
      if (formData.country === '— Others' && !formData.otherCountry) {
        alert("Please specify your country.");
        return;
      }
    } else {
      if (!formData.userType || !formData.studentId || !formData.major) {
        alert("Please fill out your Status, Student ID, and Major.");
        return;
      }
      // ✅ 국적 Others 선택 시 상세 입력 검증
      if (formData.country === '— Others' && !formData.otherCountry) {
        alert("Please specify your country.");
        return;
      }
    }
    
    // ✅ SLAM을 알게 된 경로 검증
    if (!formData.howDidYouHear) {
      alert("Please tell us how you heard about SLAM events.");
      return;
    }
    
    // 친구 추천인 경우 이름 필수
    if (formData.howDidYouHear === 'Friend referral' && !formData.friendReferralName) {
      alert("Please enter your friend's name who referred you.");
      return;
    }
    
    // 기타인 경우 상세 설명 필수
    if (formData.howDidYouHear === 'Other' && !formData.otherSource) {
      alert("Please specify how you heard about us.");
      return;
    }
    
    setStep(3); // 결제 단계로 이동 
  };

  const handleFinalSubmit = async () => {
    if (!paymentMethod) {
        return alert("Please select a payment method.");
    }
    if (paymentMethod === 'transfer' && !formData.bankLast5) {
        return alert("Please enter the last 5 digits of your bank account.");
    }

    // 현재 가격 계산
    const currentPrice = apiData.currentPrice !== undefined ? apiData.currentPrice : (apiData.earlyBirdCap && apiData.currentMembers < apiData.earlyBirdCap ? apiData.earlyBirdPrice : apiData.regularPrice);
    
    // Dietary preferences를 foodAllergies로 변환
    let foodAllergiesValue = '';
    if (formData.dietaryPreferences === 'None') {
        foodAllergiesValue = 'None';
    } else if (formData.dietaryPreferences === 'Vegetarian') {
        foodAllergiesValue = 'Vegetarian';
    } else if (formData.dietaryPreferences === 'Vegan') {
        foodAllergiesValue = 'Vegan';
    } else if (formData.dietaryPreferences === 'Food Allergies' || formData.dietaryPreferences === 'Other') {
        foodAllergiesValue = formData.dietaryDetails || formData.dietaryPreferences;
    }

    const submissionData = {
        selectedBranch: selectedBranch,
        paymentMethod: paymentMethod,
        amount: currentPrice, // 가격 정보 추가
        ...formData,
        foodAllergies: foodAllergiesValue, // 변환된 값 사용
    };

    try {
        const response = await axios.post("/api/memberships/apply", submissionData);
        alert("Application submitted successfully! Our staff will review your application and notify you soon. Thank you!");
      navigate('/events'); // ✅ 3. 신청 성공 후 이벤트 목록 페이지로 이동합니다.
    } catch (error) {
        // ✅ 중복 신청 체크
        if (error.response?.status === 400 && error.response?.data?.includes("already applied")) {
            alert("You have already applied for membership to this branch. Please wait for approval.");
        } else {
        alert("An error occurred during submission: " + (error.response?.data || error.message));
        }
    }
  };

  const renderInfoForm = () => (
    <form onSubmit={handleInfoSubmit} className="space-y-4">
      <h3 className="text-xl font-bold mb-4">Tell us about yourself</h3>
      {selectedBranch === 'TAIPEI' ? (
        <>
          <select name="professionalStatus" value={formData.professionalStatus} onChange={handleChange} className="w-full p-2 border rounded" required>
            <option value="">Your Current Status?</option>
            {taipeiStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          
          {/* ✅ 산업 카테고리 선택 */}
          {formData.professionalStatus && formData.professionalStatus !== 'Student' && (
            <>
              <select name="industry" value={formData.industry} onChange={handleChange} className="w-full p-2 border rounded bg-blue-50 focus:bg-white focus:ring-2 focus:ring-blue-200" required>
                <option value="">Select your industry</option>
                {industryCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {formData.industry === 'Others' && (
                <input 
                  type="text" 
                  name="otherIndustry" 
                  placeholder="Please specify your industry" 
                  value={formData.otherIndustry}
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-yellow-50 focus:bg-white focus:ring-2 focus:ring-yellow-200" 
                  required
                />
              )}
            </>
          )}

          {/* ✅ Goal 선택 (Professional 계열만 표시) */}
          {formData.professionalStatus && formData.professionalStatus !== 'Student' && (
            <>
              <select name="networkingGoal" value={formData.networkingGoal} onChange={handleChange} className="w-full p-2 border rounded bg-green-50 focus:bg-white focus:ring-2 focus:ring-green-200" required>
                <option value="">What's your main goal?</option>
                {/* 비즈니스 네트워킹 */}
                <option value="Business Partner">Business Partner</option>
                <option value="Client / Customer">Client / Customer</option>
                <option value="Employee / Team Member">Employee / Team Member</option>
                <option value="Investor">Investor</option>
                <option value="Mentor / Advisor">Mentor / Advisor</option>
                {/* 일반 네트워킹 */}
                <option value="Expand Social Network">Expand Social Network</option>
                <option value="Make New Friends">Make New Friends</option>
                <option value="Find Hobby Partners">Find Hobby Partners</option>
                {/* 데이팅 & 관계 */}
                <option value="Dating & Relationships">Dating & Relationships</option>
                <option value="Find Life Partner">Find Life Partner</option>
                {/* 기타 */}
                <option value="Cultural Exchange">Cultural Exchange</option>
                <option value="Language Exchange">Language Exchange</option>
                <option value="Other">Other</option>
              </select>

              {/* ✅ Other 선택 시 텍스트 입력 필드 */}
              {formData.networkingGoal === 'Other' && (
                <input 
                  type="text" 
                  name="otherNetworkingGoal" 
                  placeholder="Please specify your goal..." 
                  value={formData.otherNetworkingGoal}
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-yellow-50 focus:bg-white focus:ring-2 focus:ring-yellow-200 mt-2" 
                  required
                />
              )}
            </>
          )}
          <select name="userType" value={formData.userType} onChange={handleChange} className="w-full p-2 border rounded" required>
              <option value="">Where are you from?</option>
              <option value="taiwan">Taiwan</option>
              <option value="foreigner">International</option>
          </select>
          {formData.userType === 'foreigner' && (
            <>
            <select name="country" value={formData.country} onChange={handleChange} className="w-full p-2 border rounded" required>
              <option value="">Select your country</option>
              {countryOptions.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
              {formData.country === '— Others' && (
                <input 
                  type="text" 
                  name="otherCountry" 
                  placeholder="Please specify your country" 
                  value={formData.otherCountry}
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-yellow-50 focus:bg-white focus:ring-2 focus:ring-yellow-200" 
                  required
                />
              )}
            </>
          )}
          <input type="text" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="w-full p-2 border rounded" required />
        </>
      ) : (
        <>
          <select name="userType" value={formData.userType} onChange={handleChange} className="w-full p-2 border rounded" required>
            <option value="">Who are you?</option>
            <option value="local">Local Student</option>
            <option value="international">International Student</option>
            <option value="exchange">Exchange Student</option>
            <option value="chinese-language">Chinese Language Student</option>
          </select>
          {(formData.userType === 'international' || formData.userType === 'exchange' || formData.userType === 'chinese-language') && (
            <>
            <select name="country" value={formData.country} onChange={handleChange} className="w-full p-2 border rounded" required>
              <option value="">Select your country</option>
              {countryOptions.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
              {formData.country === '— Others' && (
                <input 
                  type="text" 
                  name="otherCountry" 
                  placeholder="Please specify your country" 
                  value={formData.otherCountry}
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-yellow-50 focus:bg-white focus:ring-2 focus:ring-yellow-200" 
                  required
                />
              )}
            </>
          )}
          <input type="text" name="studentId" placeholder="Student ID" value={formData.studentId} onChange={handleChange} className="w-full p-2 border rounded" required />
          <select name="major" value={formData.major} onChange={handleChange} className="w-full p-2 border rounded" required>
            <option value="">Select your field of study</option>
            {unifiedMajors.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {formData.major === 'Others' && (
            <input 
              type="text" 
              name="otherMajor" 
              placeholder="Please specify your field of study" 
              value={formData.otherMajor}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-2" 
              required
            />
          )}
          {/* ✅ 세부 전공 입력 (선택사항) - 대분류 선택 후 나타남 */}
          {formData.major && formData.major !== 'Others' && (
            <input 
              type="text" 
              name="detailedMajor" 
              placeholder="Detailed Major (Optional) - e.g., Computer Science, Business Administration, International Relations" 
              value={formData.detailedMajor}
              onChange={handleChange}
              className="w-full p-2 border rounded text-sm text-gray-700 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-200" 
            />
          )}
          <input type="text" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="w-full p-2 border rounded" />
        </>
      )}
      {/* Dietary Preferences Section */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Dietary Preferences / Food Restrictions
        </label>
        <select 
          name="dietaryPreferences" 
          value={formData.dietaryPreferences} 
          onChange={handleChange}
          className="w-full p-2 border rounded"
        >
          <option value="">Select an option</option>
          <option value="None">None</option>
          <option value="Vegetarian">Vegetarian</option>
          <option value="Vegan">Vegan</option>
          <option value="Food Allergies">Food Allergies</option>
          <option value="Other">Other</option>
        </select>
        
        {(formData.dietaryPreferences === 'Food Allergies' || formData.dietaryPreferences === 'Other') && (
          <input 
            type="text" 
            name="dietaryDetails" 
            placeholder={formData.dietaryPreferences === 'Food Allergies' ? 'Please specify your food allergies' : 'Please specify your dietary restrictions'}
            value={formData.dietaryDetails} 
            onChange={handleChange} 
            className="w-full p-2 border rounded" 
          />
        )}
      </div>
      
      {/* ✅ 네트워킹 목적 섹션 (Taipei 전용) */}
      {selectedBranch === 'TAIPEI' && formData.professionalStatus && formData.professionalStatus !== 'Student' && (
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
            <span className="mr-2">💼</span>
            Business Connections (Optional)
          </h4>
          <p className="text-xs text-purple-600 mb-4 italic">
            For business owners and professionals looking to expand their network with specific goals
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                👉 I am looking to connect with:
              </label>
              <select 
                name="networkingGoal" 
                value={formData.networkingGoal} 
                onChange={handleChange} 
                className="w-full p-2 border rounded bg-white focus:ring-2 focus:ring-purple-200"
              >
                <option value="">Select your networking goal (optional)</option>
                <option value="Business Partner">🤝 Business Partner</option>
                <option value="Client / Customer">💼 Client / Customer</option>
                <option value="Employee / Team Member">👥 Employee / Team Member</option>
                <option value="Investor">💰 Investor</option>
                <option value="Mentor / Advisor">🎯 Mentor / Advisor</option>
              </select>
            </div>
            
            {formData.networkingGoal && (
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-2">
                  👉 Tell us briefly what you're looking for and how both sides can benefit
                </label>
                <textarea
                  name="networkingDescription"
                  value={formData.networkingDescription}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Looking for a marketing partner to expand my café brand. I can offer event spaces in return."
                  className="w-full p-3 border rounded focus:ring-2 focus:ring-purple-200 text-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ✅ SLAM을 알게 된 경로 질문 */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
        <h4 className="font-semibold text-gray-800 mb-3">How did you hear about SLAM events?</h4>
        <select 
          name="howDidYouHear" 
          value={formData.howDidYouHear} 
          onChange={handleChange} 
          className="w-full p-2 border rounded mb-3" 
          required
        >
          <option value="">Please select one...</option>
          <option value="Instagram">📱 Instagram</option>
          <option value="Facebook">📘 Facebook</option>
          <option value="Friend referral">👥 Friend referral</option>
          <option value="Club expo">🎪 Club expo</option>
          <option value="Joined Previous membership">🔄 Joined Previous membership</option>
          <option value="Other">✏️ Other</option>
        </select>
        
        {/* 친구 추천인 이름 입력 */}
        {formData.howDidYouHear === 'Friend referral' && (
          <input 
            type="text" 
            name="friendReferralName" 
            placeholder="Friend's name who referred you"
            value={formData.friendReferralName} 
            onChange={handleChange} 
            className="w-full p-2 border rounded mt-2" 
            required
          />
        )}
        
        {/* 기타 소스 입력 */}
        {formData.howDidYouHear === 'Other' && (
          <input 
            type="text" 
            name="otherSource" 
            placeholder="Please specify how you heard about us"
            value={formData.otherSource} 
            onChange={handleChange} 
            className="w-full p-2 border rounded mt-2" 
            required
          />
        )}
      </div>
      
      <button type="submit" className="mt-8 w-full py-3 bg-blue-600 text-white font-bold rounded hover:bg-blue-700">Next: Select Payment Method</button>
    </form>
  );

    const renderPaymentForm = () => {
    const price = apiData.currentPrice !== undefined ? apiData.currentPrice : (apiData.earlyBirdCap && apiData.currentMembers < apiData.earlyBirdCap ? apiData.earlyBirdPrice : apiData.regularPrice);
    
    // Waiting list 상태 확인
    const buttonState = getMembershipButtonState();
    const isWaitingList = buttonState.text === 'Join Waiting List';
    
    // Waiting list일 때 기본적으로 Cash 선택
    if (isWaitingList && !paymentMethod) {
        setPaymentMethod('cash');
    }
    
    return (
        <div className="w-full">
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">Choose Your Payment Method</h3>
            
            <div className="space-y-4 mb-8">
                {/* Bank Transfer 옵션 - Waiting list가 아닐 때만 표시 */}
                {!isWaitingList && (
                <div 
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
                        paymentMethod === 'transfer' 
                            ? 'border-blue-500 bg-blue-50 shadow-lg' 
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => setPaymentMethod('transfer')}
                >
                    <div className="flex items-start space-x-4">
                        <div className="text-3xl">💳</div>
                        <div className="flex-1">
                            <div className="font-semibold text-gray-800 text-lg">Bank Transfer</div>
                            <div className="text-sm text-gray-600 mt-1">Secure online payment via bank transfer</div>
                            <div className="text-xs text-blue-600 mt-2">Quick and convenient</div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 ${
                            paymentMethod === 'transfer' 
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-300'
                        } flex items-center justify-center`}>
                            {paymentMethod === 'transfer' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                        </div>
                    </div>
                </div>
                )}
                
                <div 
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
                        paymentMethod === 'cash' 
                            ? 'border-green-500 bg-green-50 shadow-lg' 
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => setPaymentMethod('cash')}
                >
                    <div className="flex items-start space-x-4">
                        <div className="text-3xl">💵</div>
                        <div className="flex-1">
                            <div className="font-semibold text-gray-800 text-lg">Cash Payment</div>
                            <div className="text-sm text-gray-600 mt-1">Pay in person at your first event</div>
                            <div className="text-xs text-green-600 mt-2">Flexible and easy</div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 ${
                            paymentMethod === 'cash' 
                                ? 'border-green-500 bg-green-500' 
                                : 'border-gray-300'
                        } flex items-center justify-center`}>
                            {paymentMethod === 'cash' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                        </div>
                    </div>
                </div>
            </div>

            {paymentMethod === 'transfer' && !isWaitingList && (
                <div className="mt-6 p-6 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="text-center mb-4">
                        <p className="font-bold text-blue-800 text-lg">📋 Transfer Details</p>
                    </div>
                    <div className="space-y-2 text-sm bg-white p-4 rounded-lg border border-blue-100">
                        <p><strong>Amount:</strong> {price === 0 ? 'FREE' : `${price} NTD`}</p>
                        {apiData.bankName && (
                            <p><strong>Bank Name:</strong> {apiData.bankName}</p>
                        )}
                        <p><strong>Account:</strong> {eventBankInfo?.account || "123-456-7890"}</p>
                        {apiData.accountName && (
                            <p><strong>Account Name:</strong> {apiData.accountName}</p>
                        )}
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-blue-800 mb-2">
                            Last 5 digits of your bank account (for verification):
                        </label>
                <input
                  type="text"
                  name="bankLast5"
                            placeholder="e.g., 12345"
                  value={formData.bankLast5}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData(prev => ({ ...prev, bankLast5: onlyDigits }));
                  }}
                            className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center font-mono text-lg"
                  inputMode="numeric"
                  pattern="\\d{5}"
                  maxLength={5}
                  required
                />
                    </div>
                </div>
            )}
            
            {paymentMethod === 'cash' && (
                <div className="mt-6 p-6 bg-green-50 rounded-xl border border-green-200">
                    <div className="text-center">
                        <p className="font-bold text-green-800 text-lg">💵 Cash Payment Details</p>
                        <div className="mt-4 p-4 bg-white rounded-lg border border-green-100">
                            <p className="font-bold text-green-700">Amount: {price === 0 ? 'FREE' : `${price} NTD`}</p>
                            <p className="text-sm text-green-600 mt-2">
                                Pay at your first event or during designated collection times
                            </p>
                            <p className="text-xs text-gray-600 mt-2">
                                ✅ We'll send you collection details via email after registration
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="text-center mt-8">
                <button 
                    onClick={handleFinalSubmit} 
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-lg rounded-xl hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    disabled={!paymentMethod}
                >
                    {paymentMethod ? '🎉 Complete Application' : '⬆️ Please select a payment method'}
            </button>
            </div>
        </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 sm:py-12 sm:px-6 lg:py-16 lg:px-8">
      <div className="w-full max-w-4xl mx-auto">
        {step === 0 && (
          <div className="text-center py-8">
            <h2 className="text-2xl font-bold text-red-600 mb-4">⚠️ Direct Access Not Allowed</h2>
            <p className="text-gray-600 mb-6">Please select a branch from the Events page to join membership.</p>
            <Link 
              to="/events" 
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-block"
            >
              Go to Events Page
            </Link>
          </div>
        )}
        {step === 1 && (
          <div>
            <SlamPromotionCard
              data={{...apiData, selectedBranch, registrationCloseDate: apiData.registrationCloseDate || branchCloseDate}}
              onRegisterClick={() => {
                const buttonState = getMembershipButtonState();
                if (buttonState.text === 'View Events & RSVP') {
                  // 멤버십이 승인된 경우 Events 페이지로 이동
                  navigate('/events');
                } else if (buttonState.text === 'Join Waiting List') {
                  // Registration이 마감된 경우에도 Application Form으로 이동
                  setStep(2);
                } else {
                  // 일반적인 경우 정보 입력 단계로 이동
                  setStep(2);
                }
              }}
              buttonState={getMembershipButtonState()}
              membershipStatus={membershipStatus}
            />

            <div className="text-center mt-6">
              <Link
                to="/events"
                className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 hover:underline transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Events</span>
              </Link>
            </div>
          </div>
        )}
        {step === 2 && (
            <div>
            {/* 정보 입력 폼을 카드로 감싸기 */}
            <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8 lg:p-10 max-w-2xl mx-auto">
            {renderInfoForm()}
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 hover:underline transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Promotion</span>
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
            <div>
            {/* 결제 폼을 카드로 감싸기 */}
            <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8 lg:p-10 max-w-2xl mx-auto">
                {renderPaymentForm()}
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 hover:underline transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to edit info</span>
              </button>
            </div>
            </div>
        )}
      </div>
    </div>
  );
}